import { useDocumentTitle } from "@/hooks/use-document-title";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useListIntegrations, useListIntegrationDefinitions } from "@/queries/integration-queries";
import {
    useListHostAuthenticationTokens,
    useDeleteHostAuthenticationToken,
} from "@/queries/runner-configuration-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { HostAuthenticationToken } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { Integration, IntegrationDefinition } from "gitpod-next-api/gitpod/v1/integration_pb";
import type { FC } from "react";
import { useState, useCallback, useMemo } from "react";
import { useDesktop } from "@/hooks/use-desktop";
import { appMainService } from "@/ipc";

type IntegrationWithAuth = {
    integration: PlainMessage<Integration>;
    definition?: PlainMessage<IntegrationDefinition>;
    authToken?: PlainMessage<HostAuthenticationToken>;
    isConnected: boolean;
};

export type IntegrationsPageProps = {
    /** When true, hides the description text (used in modal context where description is provided externally) */
    hideDescription?: boolean;
};

export const IntegrationsPage: FC<IntegrationsPageProps> = ({ hideDescription = false }) => {
    useDocumentTitle("Integrations");
    const { toast } = useToast();
    const { isDesktop } = useDesktop();

    const { data: user, isLoading: isLoadingUser } = useAuthenticatedUser();
    const {
        data: integrations,
        isLoading: isLoadingIntegrations,
        error: integrationsError,
    } = useListIntegrations(user?.organizationId);
    const {
        data: integrationDefinitions,
        isLoading: isLoadingDefinitions,
        error: definitionsError,
    } = useListIntegrationDefinitions();
    const { data: tokensData, isLoading: isLoadingTokens, error: tokensError } = useListHostAuthenticationTokens({});

    const deleteTokenMutation = useDeleteHostAuthenticationToken();

    const [connectingIntegrationId, setConnectingIntegrationId] = useState<string | null>(null);
    const [disconnectingIntegrationId, setDisconnectingIntegrationId] = useState<string | null>(null);

    const isLoading = isLoadingUser || isLoadingIntegrations || isLoadingDefinitions || isLoadingTokens;
    const error = integrationsError || definitionsError || tokensError;

    const availableIntegrations = integrations || [];

    const definitionsMap = useMemo(() => {
        const map = new Map<string, PlainMessage<IntegrationDefinition>>();
        integrationDefinitions?.forEach((definition) => {
            map.set(definition.id, definition);
        });
        return map;
    }, [integrationDefinitions]);

    const authTokensMap = useMemo(() => {
        const map = new Map<string, PlainMessage<HostAuthenticationToken>>();
        tokensData?.tokens?.forEach((token) => {
            if (token.integrationId) {
                map.set(token.integrationId, token);
            }
        });
        return map;
    }, [tokensData]);

    const integrationsWithAuth: IntegrationWithAuth[] = availableIntegrations.map((integration) => {
        const definition = definitionsMap.get(integration.integrationDefinitionId);
        const authToken = authTokensMap.get(integration.id);
        return {
            integration,
            definition,
            authToken,
            isConnected: !!authToken,
        };
    });

    const handleConnect = useCallback(
        (integrationWithAuth: IntegrationWithAuth) => {
            if (!user?.id) return;

            const { integration } = integrationWithAuth;

            if (!integration.enabled) {
                toast({
                    title: "Integration disabled",
                    description: "This integration is currently disabled and cannot be connected to.",
                });
                return;
            }

            setConnectingIntegrationId(integration.id);

            let authUrl = `/integrations/oauth/${integration.id}/authorize?principal=${encodeURIComponent(`user/${user.id}`)}`;

            // Make authUrl absolute if it's relative
            if (authUrl.startsWith("/")) {
                authUrl = `${window.location.origin}${authUrl}`;
            }

            // Open in external browser for desktop app, otherwise use window.open
            if (isDesktop && appMainService) {
                void appMainService.openExternal({ url: authUrl });
            } else {
                window.open(authUrl, "_blank", "noopener");
            }

            // Reset connecting state after a short delay since we can't monitor the popup
            // The UI will automatically update when useListHostAuthenticationTokens detects the new token
            setTimeout(() => {
                setConnectingIntegrationId(null);
            }, 1000);

            toast({
                title: "OAuth window opened",
                description:
                    "Complete the authorization in the popup window. Your connection status will update automatically.",
            });
        },
        [user?.id, toast, isDesktop],
    );

    const handleDisconnect = useCallback(
        async (integrationWithAuth: IntegrationWithAuth) => {
            if (!integrationWithAuth.authToken) return;

            setDisconnectingIntegrationId(integrationWithAuth.integration.id);

            try {
                await deleteTokenMutation.mutateAsync({
                    tokenId: integrationWithAuth.authToken.id,
                });

                toast({
                    title: "Integration disconnected",
                    description: "Your account has been disconnected from this integration.",
                });
            } catch (error) {
                console.error("Failed to disconnect integration:", error);
                toast({
                    title: "Disconnection failed",
                    description: "Failed to disconnect the integration. Please try again.",
                    variant: "default",
                });
            } finally {
                setDisconnectingIntegrationId(null);
            }
        },
        [deleteTokenMutation, toast],
    );

    return (
        <div data-testid="user-integrations">
            <ErrorMessage error={error} />
            <SkeletonBlock ready={!isLoading} className="h-32 w-full">
                <div className={hideDescription ? "" : "flex flex-col gap-8"}>
                    {!hideDescription && (
                        <Text className="text-base text-content-secondary">
                            Connect your personal accounts to integrations enabled by your organization.
                        </Text>
                    )}

                    {!error && (
                        <>
                            {integrationsWithAuth.length === 0 && (
                                <EmptyStateCard
                                    title="No integrations available"
                                    description="Your organization hasn't enabled any integrations yet. Contact your admin to enable integrations."
                                    data-testid="no-integrations-available"
                                />
                            )}

                            {integrationsWithAuth.length > 0 && (
                                <div className="flex flex-col gap-2">
                                    {integrationsWithAuth.map((integrationWithAuth) => (
                                        <IntegrationRow
                                            key={integrationWithAuth.integration.id}
                                            integrationWithAuth={integrationWithAuth}
                                            onConnect={() => handleConnect(integrationWithAuth)}
                                            onDisconnect={() => handleDisconnect(integrationWithAuth)}
                                            isConnecting={
                                                connectingIntegrationId === integrationWithAuth.integration.id
                                            }
                                            isDisconnecting={
                                                disconnectingIntegrationId === integrationWithAuth.integration.id
                                            }
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </SkeletonBlock>
        </div>
    );
};

type IntegrationRowProps = {
    integrationWithAuth: IntegrationWithAuth;
    onConnect: () => void;
    onDisconnect: () => void;
    isConnecting: boolean;
    isDisconnecting: boolean;
};

const IntegrationRow: FC<IntegrationRowProps> = ({
    integrationWithAuth,
    onConnect,
    onDisconnect,
    isConnecting,
    isDisconnecting,
}) => {
    const { integration, definition, isConnected } = integrationWithAuth;
    const isDisabled = !integration.enabled;

    return (
        <div className="flex items-center justify-between gap-2 rounded-xl border-0.5 border-border-base bg-surface-primary px-4 py-3 @container">
            <div className="flex items-center gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg">
                    {definition?.iconUrl ? (
                        <img src={definition.iconUrl} alt={`${definition.name} icon`} className="size-8" />
                    ) : (
                        <div className="size-6 rounded bg-content-secondary" />
                    )}
                </div>
                <div className="flex flex-col">
                    <Text className={`text-base font-bold ${isDisabled ? "text-content-tertiary" : ""}`}>
                        {definition?.name || integration.integrationDefinitionId}
                    </Text>
                    {(definition?.description || "Organization integration") && (
                        <Text className="hidden text-sm text-content-secondary @sm:block">
                            {definition?.description || "Organization integration"}
                        </Text>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2">
                {isConnected ? (
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={onDisconnect}
                        disabled={isDisconnecting}
                        data-testid={`disconnect-${integration.id}`}
                        data-tracking-id="ondisconnect"
                    >
                        {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onClick={onConnect}
                        disabled={isConnecting || isDisabled}
                        data-testid={`connect-${integration.id}`}
                        data-tracking-id="onconnect"
                    >
                        {isConnecting ? "Connecting..." : "Connect"}
                    </Button>
                )}
            </div>
        </div>
    );
};
